import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/config";
import StoreBadges from "@/components/site/StoreBadges";
import { SITE } from "@/constants/testIds";

const FOOTER_COLUMNS = [
  {
    heading: "Situations",
    links: [
      { label: "Falsely accused", to: "/accused" },
      { label: "If you go missing", to: "/missing" },
      { label: "Altercations", to: "/altercations" },
    ],
  },
  {
    heading: "Product",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "Security & Trust", to: "/security" },
      { label: "The Undertaking", to: "/undertaking" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Stories", to: "/stories" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-[color:var(--yn-ink)] text-white mt-24"
      data-testid={SITE.footer}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link to="/" className="flex items-center gap-3">
              <img
                src={BRAND.logoUrl}
                alt=""
                className="h-10 w-10 rounded-md object-cover"
                loading="lazy"
                decoding="async"
              />
              <span className="font-heading text-xl font-bold">YeboNadi</span>
            </Link>
            <p className="mt-5 font-heading text-lg sm:text-xl leading-snug max-w-md">
              {BRAND.tagline}
            </p>

            <div className="mt-8">
              <p className="text-xs font-medium uppercase tracking-wider text-white/60 mb-3">
                Install the app
              </p>
              <StoreBadges
                placement="footer"
                appTestId={SITE.footerAppStoreBadge}
                playTestId={SITE.footerPlayStoreBadge}
              />
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.heading}>
                <p className="text-xs font-medium uppercase tracking-wider text-white/60 mb-4">
                  {col.heading}
                </p>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="text-sm text-white/85 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs font-medium uppercase tracking-wider text-white/60 mb-4">
              Contact
            </p>
            <address className="not-italic space-y-3 text-sm text-white/85">
              <div className="flex items-start gap-2.5">
                <MapPin size={16} className="mt-0.5 shrink-0 text-white/60" />
                <span>
                  {BRAND.parent}
                  <br />
                  {BRAND.address}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail size={16} className="shrink-0 text-white/60" />
                <a
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-white"
                  data-testid={SITE.footerEmail}
                >
                  {BRAND.email}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone size={16} className="shrink-0 text-white/60" />
                <a
                  href={`tel:${BRAND.phone.replace(/[^+\d]/g, "")}`}
                  className="hover:text-white"
                  data-testid={SITE.footerPhone}
                >
                  {BRAND.phone}
                </a>
              </div>
            </address>
          </div>
        </div>

        <div
          className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-start md:justify-between gap-6"
        >
          <p
            className="text-xs text-white/60 max-w-2xl leading-relaxed"
            data-testid={SITE.footerDisclaimer}
          >
            {BRAND.legalDisclaimer}
          </p>
          <p className="text-xs text-white/50" data-testid={SITE.footerOwnership}>
            © {year} {BRAND.parent}. All rights reserved. YeboNadi and
            yebonadi.com are owned and operated by {BRAND.parent}.
          </p>
        </div>

        <nav
          className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/50"
          aria-label="Sitemap"
        >
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-white/80">
              {l.label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}
